<template>
  <b-card body-bg-variant="light"
          header="Published papers by the proposers as a result of experiment/s done at the SOLARIS"
          header-bg-variant="dark"
          header-text-variant="light">

    <label for="anyPublicationsRelatedToSolaris">
      Have you published any research in the last 4 years as a result of experiment(s) performed using the SOLARIS
      infrastructure?
    </label>
    <extended-radio-group id="anyPublicationsRelatedToSolaris"
                          v-model="data.general.any_publications_related_to_solaris"
                          :disabled="!editable"
                          :options="yesNo"
                          size="lg"/>

    <template v-if="shouldPublicationsFieldBeVisible">
      <p class="p-form-label">
        Please give the references of papers published by the proposers during the past 4 years
      </p>
      <p class="p-form-description">{{ publicationPattern }} </p>
      <b-input-group class="mb-0">
        <b-form-input v-model="currentPublicationData"
                      :class="['form-control', {'custom-invalid-input-border': markPublicationDataFieldAsInvalid}]"
                      :readonly="!editable"
                      placeholder="Enter publication"
                      type="text"/>
        <b-input-group-append>
          <extended-radio-group id="publications-source"
                                v-model="currentPublicationSource"
                                :disabled="!editable"
                                :options="publicationSourceOptions"
                                size="md"/>
        </b-input-group-append>
      </b-input-group>

      <p class="p-form-description mt-3">DOI link</p>
      <b-input-group class="mb-3">
        <b-form-input v-model="currentPublicationDoi"
                      :class="['form-control', 'mr-2', {'custom-invalid-input-border': markPublicationDoiFieldAsInvalid}]"
                      :readonly="!editable"
                      placeholder="Enter DOI link"
                      type="text"/>
        <b-input-group-append>
          <b-button :disabled="shouldAddPublicationButtonBeDisabled"
                    variant="info"
                    @click="addPublication">
            Add publication
          </b-button>
        </b-input-group-append>
      </b-input-group>

      <b-card v-for="(singlePublication, index) in data.general.publications"
              :key="singlePublication.publication_doi + '_' + index"
              bg-variant="white"
              body-class="pa-1"
              class="ma-0 mt-3 w-100">
        <b-card-body class="d-flex align-items-center pa-0 pl-2">
          <span class="w-100 mr-2">
            {{ singlePublication.publication_data }}, DOI: {{ singlePublication.publication_doi }}
          </span>
          <base-icon-button-close :disabled="!editable" @click="removePublication(singlePublication)"/>
        </b-card-body>
      </b-card>
    </template>
  </b-card>
</template>

<script>
  import yesNo from '@/json/radioButtonYesNo';
  import ExtendedRadioGroup from '@/components/ExtendedRadioGroup';
  import BaseIconButtonClose from '@/components/base-components/base-icons/BaseIconButtonClose';

  export default {
    name: 'PublicationsSection',
    props: {
      value: {
        type: Object,
        required: true,
      },
      validation: {
        type: Object,
        required: true,
      },
      editable: {
        type: Boolean,
        default: true,
      },
    },
    components: { BaseIconButtonClose, ExtendedRadioGroup },
    data() {
      return {
        data: this.value,
        $v: this.validation,
        yesNo,
        publicationPattern: 'Pattern: Author(s) Last Name and First Name, Title of Article, Journal Title, '
          + 'Volume and issue number, Page numbers, (Publication date)',
        publicationSourceOptions: [
          {
            text: 'SOLARIS infrastructure ONLY',
            value: 'data from SOLARIS infrastructure ONLY',
            buttonVariant: 'outline-info',
          },
          {
            text: 'More than one source',
            value: 'data from more than one source',
            buttonVariant: 'outline-info',
          },
        ],
        currentPublicationSource: '',
        currentPublicationData: '',
        currentPublicationDoi: '',
      };
    },
    computed: {
      shouldPublicationsFieldBeVisible() {
        return this.data.general.any_publications_related_to_solaris;
      },
      shouldAddPublicationButtonBeDisabled() {
        return !this.currentPublicationSource || !this.currentPublicationData || !this.currentPublicationDoi;
      },
      isPublicationsListEmpty() {
        return !this.data.general.publications || this.data.general.publications.length === 0;
      },
      markPublicationDataFieldAsInvalid() {
        return (this.isPublicationsListEmpty && this.currentPublicationData === '') ?? true;
      },
      markPublicationDoiFieldAsInvalid() {
        return (this.isPublicationsListEmpty && this.currentPublicationDoi === '') ?? true;
      },
    },
    methods: {
      addPublication() {
        const currentPublication = {
          /* eslint-disable camelcase */
          publication_data: `${this.currentPublicationData} - ${this.currentPublicationSource}`,
          publication_doi: this.currentPublicationDoi,
          /* eslint-enable camelcase */
        };
        if (!this.data.general.publications) {
          this.$set(this.data.general, 'publications', []);
        }
        this.data.general.publications = Array.isArray(this.data.general.publications)
          ? this.data.general.publications
          : [];
        this.data.general.publications.push(currentPublication);
        this.clearCurrentPublicationFields();
      },
      removePublication(publicationToRemove) {
        this.data.general.publications = this.data.general.publications.filter(
          (publicationFromList) => publicationFromList.publication_doi !== publicationToRemove.publication_doi,
        );
      },
      clearCurrentPublicationFields() {
        this.currentPublicationSource = '';
        this.currentPublicationData = '';
        this.currentPublicationDoi = '';
      },
    },
    watch: {
      value: {
        handler() {
          this.data = this.value;
          if (this.data.general === undefined) {
            this.data.general = {};
          }
        },
        deep: true,
      },
      'data.general.any_publications_related_to_solaris': {
        handler() {
          if (!this.data.general.any_publications_related_to_solaris) {
            delete this.data.general.publications;
          }
        },
        deep: false,
      },
    },
  };
</script>

<style scoped>
  ::v-deep #publications-source #radio-group {
    margin-bottom: 0 !important;
  }
</style>
